import { useCallback } from "react"
import { AssetVersionsGallery } from "@/components/media/asset-versions-gallery"
import { useAssetVersionsController } from "@/components/media/use-asset-versions-controller"
import { useMediaGalleryLabels } from "@/components/media/use-media-gallery-labels"
import { useTranslation } from "@/hooks/use-translation"
import { activateFlowNodeVersion, listFlowNodeVersions, unlinkFlowNodeVersion } from "@/lib/flow-api"
import type { FlowNodeRow } from "@/types/flow"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  node: FlowNodeRow
  onChanged?: () => void
}

export function FlowNodeVersionsGallery({ open, onOpenChange, projectId, node, onChanged }: Props) {
  const { t } = useTranslation(["flow", "workspace", "common", "location"])
  const list = useCallback(() => listFlowNodeVersions(projectId, node.id), [projectId, node.id])
  const activate = useCallback(
    (versionId: number) => activateFlowNodeVersion(projectId, node.id, versionId),
    [projectId, node.id],
  )
  const remove = useCallback(
    (versionId: number) => unlinkFlowNodeVersion(projectId, node.id, versionId),
    [projectId, node.id],
  )
  const { versions, loading, busyId, error, handleActivate, handleDelete } = useAssetVersionsController({
    open,
    list,
    activate,
    remove,
    deleteConfirm: t("versions.deleteConfirm"),
    onChanged,
  })
  const labels = useMediaGalleryLabels()

  const prompt = node.prompt_text.trim()

  return (
    <AssetVersionsGallery
      open={open}
      onOpenChange={onOpenChange}
      title={t("versions.title")}
      description={prompt || undefined}
      closeAriaLabel={t("close", { ns: "common" })}
      hint={t("charactersPage.mediaGalleryHint", { ns: "workspace" })}
      versions={versions}
      loading={loading}
      error={error}
      busyId={busyId}
      labels={labels}
      previewMode={
        node.kind === "video" ||
        node.kind === "video_transition" ||
        node.kind === "video_upscale" ||
        node.kind === "video_depth" ||
        node.chain_output_media === "video"
          ? "video"
          : "image"
      }
      previewChrome="cover"
      onActivate={(id) => void handleActivate(id)}
      onDelete={(id) => void handleDelete(id)}
    />
  )
}
